"use client";

import { type ListBoxProps, type ListBoxSectionProps } from "@heroui/react";
import React from "react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { useSidebar } from "./SidebarContext";

export enum SidebarItemType {
  Nest = "nest",
}

export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  img?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
};

export type SidebarProps = Omit<ListBoxProps<SidebarItem>, "children"> & {
  items: SidebarItem[];
  isCompact?: boolean;
  hideEndContent?: boolean;
  iconClassName?: string;
  sectionClasses?: ListBoxSectionProps["classNames"];
  classNames?: ListBoxProps<object>["classNames"];
  itemClasses?: ListBoxProps<SidebarItem>["itemClasses"] & {
    brandTitle?: string;
  };
  defaultSelectedKey: string;
  onSelect?: (key: string) => void;
  handleNewChat?: () => void;
  itemClicked?: (item: SidebarItem) => void;
  showMenuTitle?: boolean;
};

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      isCompact,
      defaultSelectedKey,
      selectedKeys,
      onSelect,
      hideEndContent,
      sectionClasses: _sectionClassesProp = {},
      itemClasses: itemClassesProp = {},
      iconClassName,
      className,
      itemClicked,
      showMenuTitle,
    },
    ref,
  ) => {
    const controlledKey = React.useMemo<React.Key | undefined>(() => {
      if (!selectedKeys || selectedKeys === "all") return undefined;
      const first = Array.from(selectedKeys as Iterable<React.Key>)[0];

      return first;
    }, [selectedKeys]);
    const [internalSelected, setInternalSelected] = React.useState<React.Key>(defaultSelectedKey);
    const selected = controlledKey ?? internalSelected;
    const [failedImages, setFailedImages] = React.useState<Set<string>>(new Set());
    const { isAuthenticated } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { brand } = useBrand();
    const { setShowChatHistory } = useSidebar();

    // Don't render anything if user is not authenticated
    if (!isAuthenticated) {
      return null;
    }

    void itemClassesProp;

    const handleItemClick = (item: SidebarItem) => {
      setInternalSelected(item.key);
      onSelect?.(item.key);
      if (item.href === "/chat") setShowChatHistory(true);
      else itemClicked?.(item);
      if (item.href) navigate(item.href);
    };

    const iconSize = isCompact ? 30 : 24;

    const renderIcon = (item: SidebarItem) => {
      if (item.icon) {
        if (brand.menuItemsIcon === "image" && item.img && !failedImages.has(item.key)) {
          return (
            <img
              alt=""
              className={brand.name === "Invest Ai" ? "invest-ai-icon-filter" : undefined}
              src={item.img}
              style={{
                borderRadius: 6,
                width: iconSize,
                height: iconSize,
                minWidth: iconSize,
                objectFit: "contain",
              }}
              onError={() =>
                setFailedImages((prev) => {
                  const next = new Set(prev);

                  next.add(item.key);

                  return next;
                })
              }
            />
          );
        }

        return (
          <Icon
            className={cn("text-default-500 shrink-0", iconClassName)}
            icon={item.icon}
            style={{ minWidth: iconSize }}
            width={iconSize}
          />
        );
      }

      return item.startContent ?? null;
    };

    const renderItem = (item: SidebarItem) => {
      const isNestType = item.items && item.items.length > 0 && item?.type === SidebarItemType.Nest;

      if (isNestType) {
        return renderNestItem(item);
      }

      const isSelected = selected === item.key;

      return (
        <button
          key={item.key}
          aria-label={t(item.title)}
          aria-selected={isSelected}
          className={cn(
            "flex items-center gap-2 w-full rounded-lg px-3 py-2 text-left transition-colors cursor-pointer font-medium",
            isSelected
              ? "bg-default-300 text-foreground dark:bg-default-200"
              : "text-default-500 hover:bg-default-100",
            { "justify-center px-2 py-3": isCompact },
          )}
          data-id={`menu-${item.key}`}
          role="option"
          type="button"
          onClick={() => handleItemClick(item)}
        >
          {isCompact ? (
            <Tooltip>
              <TooltipTrigger>
                <div className="flex w-full items-center justify-center">{renderIcon(item)}</div>
              </TooltipTrigger>
              <TooltipContent placement="right">{t(item.title)}</TooltipContent>
            </Tooltip>
          ) : (
            <>
              {renderIcon(item)}
              <span className="flex-1 text-sm">{t(item.title)}</span>
              {!hideEndContent && (item.endContent ?? null)}
            </>
          )}
        </button>
      );
    };

    const renderNestItem = (item: SidebarItem) => {
      if (isCompact) {
        return (
          <div key={item.key} className="flex justify-center py-1">
            <Tooltip>
              <TooltipTrigger
                className="flex items-center justify-center p-1 rounded-lg hover:bg-default-50 cursor-pointer"
                onClick={() => item.href && navigate(item.href)}
              >
                {renderIcon(item)}
              </TooltipTrigger>
              <TooltipContent placement="right">{t(item.title)}</TooltipContent>
            </Tooltip>
          </div>
        );
      }

      return (
        <div key={item.key}>
          <details className="group/nest">
            <summary className="list-none cursor-pointer [&::-webkit-details-marker]:hidden">
              <div className="flex h-11 items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-default-50">
                {renderIcon(item)}
                <span className="text-sm font-medium text-default-500 flex-1">{t(item.title)}</span>
                <Icon
                  className="text-default-400 transition-transform group-open/nest:rotate-180"
                  icon="solar:alt-arrow-down-linear"
                  width={16}
                />
              </div>
            </summary>
            {item.items && item.items.length > 0 && (
              <div className="mt-0.5 border-l border-default-200 pl-4 ml-4">
                {item.items.map(renderItem)}
              </div>
            )}
          </details>
        </div>
      );
    };

    const renderSection = (item: SidebarItem) => {
      if (item.items && item.items.length > 0 && item.type === SidebarItemType.Nest) {
        return renderNestItem(item);
      }

      if (item.items && item.items.length > 0) {
        return (
          <div
            key={item.key}
            aria-label={`${t(item.title)} section`}
            className={cn("w-full", { "p-0 max-w-14": isCompact })}
          >
            {showMenuTitle && (
              <div className="text-xs text-default-400 px-3 py-1">{t(item.title)}</div>
            )}
            {item.items.map(renderItem)}
          </div>
        );
      }

      return renderItem(item);
    };

    return (
      <nav ref={ref as React.Ref<HTMLDivElement>}>
        <div
          aria-label={t("Navigation sidebar")}
          className={cn("list-none flex flex-col gap-0.5", className)}
          role="listbox"
        >
          {items.map(renderSection)}
        </div>
      </nav>
    );
  },
);

Sidebar.displayName = "Sidebar";

export default Sidebar;
